import React, { memo, useState } from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Ionicons } from '@expo/vector-icons';
import { Comment } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { deleteComment } from '../services/commentsService';
import PixelContainer from './PixelContainer';
import { fontStyle } from '../config/fonts';
import { formatShortTimestamp } from '../utils/formatTimestamp';

interface CommentListItemProps {
  comment: Comment;
  depth?: number;
  onReply: (parentId: string) => void;
}

const MAX_DEPTH = 5;

function CommentListItem({ comment, depth = 0, onReply }: CommentListItemProps) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { colors } = useTheme();
  const queryClient = useQueryClient();
  const [showReplies, setShowReplies] = useState(true);

  const deleteMutation = useMutation({
    mutationFn: () => deleteComment(comment.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', comment.postId] });
    },
  });

  const locale = i18n.language.startsWith('es') ? 'es' : 'en';

  const handleReply = () => {
    onReply(comment.id);
  };

  const handleDelete = () => {
    deleteMutation.mutate();
  };

  const isOwner = user?.uid === comment.userId;
  const hasReplies = comment.replies && comment.replies.length > 0;
  const marginLeft = Math.min(depth, MAX_DEPTH) * 16;

  return (
    <View style={[styles.container, { marginLeft }]}>
      {/* Left border for nested comments */}
      {depth > 0 && <View style={[styles.leftBorder, { backgroundColor: colors.border }]} />}

      <View style={styles.content}>
        {/* Header */}
        <View style={styles.header}>
          <View style={[styles.avatar, { backgroundColor: colors.surface }]} />
          <Text style={[styles.username, fontStyle, { color: colors.text }]}>User</Text>
          <Text style={[styles.dot, fontStyle, { color: colors.textSecondary }]}>•</Text>
          <Text style={[styles.timestamp, fontStyle, { color: colors.textSecondary }]}>
            {formatShortTimestamp(comment.createdAt, locale, false)}
          </Text>
        </View>

        {/* Comment text */}
        <Text style={[styles.commentText, fontStyle, { color: colors.text }]}>{comment.comment}</Text>

        {/* Actions */}
        <View style={styles.actions}>
          <PixelContainer backgroundColor={colors.surface} cornerSize={3} style={styles.voteContainer}>
            <View style={styles.voteContent}>
              <Pressable style={styles.actionButton}>
                <Ionicons name="arrow-up-outline" size={16} color={colors.textSecondary} />
              </Pressable>
              <Text style={[styles.voteCount, fontStyle, { color: colors.textSecondary }]}>0</Text>
              <Text style={[styles.voteDivider, fontStyle, { color: colors.border }]}>|</Text>
              <Pressable style={styles.actionButton}>
                <Ionicons name="arrow-down-outline" size={16} color={colors.textSecondary} />
              </Pressable>
            </View>
          </PixelContainer>

          <PixelContainer backgroundColor={colors.surface} cornerSize={3} style={styles.replyButton}>
            <Pressable style={styles.replyContent} onPress={handleReply}>
              <Ionicons name="chatbubble-outline" size={14} color={colors.textSecondary} />
              <Text style={[styles.replyText, fontStyle, { color: colors.textSecondary }]}>{t('post.reply')}</Text>
            </Pressable>
          </PixelContainer>

          {isOwner && (
            <Pressable style={styles.deleteButton} onPress={handleDelete}>
              <Ionicons name="trash-outline" size={14} color={colors.error} />
            </Pressable>
          )}
        </View>

        {/* Toggle replies */}
        {hasReplies && (
          <Pressable
            style={styles.toggleReplies}
            onPress={() => setShowReplies(!showReplies)}
          >
            <Text style={[styles.toggleText, fontStyle, { color: colors.primary }]}>
              {showReplies ? t('post.hideReplies') : t('post.showReplies')} (
              {comment.replies?.length})
            </Text>
          </Pressable>
        )}

        {/* Nested replies */}
        {showReplies && hasReplies && depth < MAX_DEPTH && (
          <View style={styles.replies}>
            {comment.replies?.map((reply) => (
              <CommentListItem
                key={reply.id}
                comment={reply}
                depth={depth + 1}
                onReply={onReply}
              />
            ))}
          </View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  leftBorder: {
    width: 2,
    marginRight: 8,
  },
  content: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  avatar: {
    width: 20,
    height: 20,
    marginRight: 6,
  },
  username: {
    fontSize: 12,
    fontWeight: '600',
  },
  dot: {
    fontSize: 12,
    marginHorizontal: 4,
  },
  timestamp: {
    fontSize: 12,
  },
  commentText: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 8,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  voteContainer: {
    paddingHorizontal: 4,
    paddingVertical: 2,
  },
  voteContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  actionButton: {
    padding: 4,
  },
  voteCount: {
    fontSize: 12,
    marginHorizontal: 4,
    minWidth: 20,
    textAlign: 'center',
  },
  voteDivider: {
    fontSize: 12,
    marginHorizontal: 4,
  },
  replyButton: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    marginLeft: 8,
  },
  replyContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  replyText: {
    fontSize: 12,
    fontWeight: '500',
  },
  deleteButton: {
    padding: 4,
    marginLeft: 8,
  },
  toggleReplies: {
    marginTop: 8,
  },
  toggleText: {
    fontSize: 12,
    fontWeight: '500',
  },
  replies: {
    marginTop: 8,
  },
});

export default memo(CommentListItem);
